import { getApiBaseUrl } from '@/config/environment';
import { adminAuthHeaders } from '../utils/adminAuthHeaders';


export interface LiveMetrics {
  activeRentals: number;
  pendingHires: number;
  activeDisputes: number;
  totalRevenue24h: number;
  averageTip: number;
  fleetUtilization: number;
  customerSatisfaction: number;
  avgResponseTime: number;
}

export interface LiveLocation {
  id: string;
  type: 'active-trip' | 'driver' | 'mobile-user' | 'booking-session';
  lat: number;
  lng: number;
  label: string;
  status: string;
  platform?: 'ios' | 'android';
  activity?: string;
  duration?: string;
  userId?: string;
  bookingId?: string;
}

export interface MobileActivity {
  id: string;
  type: 'booking' | 'payment' | 'rating' | 'registration' | 'verification' | 'driver_status' | 'error';
  action: string;
  user: {
    id: string;
    name: string;
    role: 'renter' | 'host' | 'driver';
  };
  details: any;
  timestamp: string;
  platform: 'ios' | 'android';
  appVersion?: string;
}

export interface LiveOperationsData {
  metrics: LiveMetrics;
  locations: LiveLocation[];
  mobileActivity: MobileActivity[];
  lastUpdated: string;
}

class LiveOperationsService {
  private cache: LiveOperationsData | null = null;
  private lastFetch: number = 0;
  private cacheTimeout: number = 30000; // 30 seconds

  /**
   * Get live operations data with caching
   */
  async getLiveOperations(forceRefresh: boolean = false): Promise<LiveOperationsData> {
    const now = Date.now();
    
    // Return cached data if still fresh
    if (!forceRefresh && this.cache && (now - this.lastFetch) < this.cacheTimeout) {
      return this.cache;
    }

    try {
      
      const response = await fetch(`${getApiBaseUrl()}/api/v1/live-operations`, {
        method: 'GET',
        headers: adminAuthHeaders({ 'Content-Type': 'application/json', 'Accept': 'application/json' })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error('API returned unsuccessful response');
      }


      // Cache the data
      this.cache = data.data;
      this.lastFetch = now;

      return data.data;

    } catch (error) {
      console.error('❌ Error fetching live operations data:', error);
      
      // Return fallback data if API fails
      return this.getFallbackData();
    }
  }

  /**
   * Get only live metrics
   */
  async getLiveMetrics(): Promise<LiveMetrics> {
    const data = await this.getLiveOperations();
    return data.metrics;
  }

  /**
   * Get only active locations
   */
  async getActiveLocations(): Promise<LiveLocation[]> {
    const data = await this.getLiveOperations();
    return data.locations;
  }

  /**
   * Get only mobile activity
   */
  async getMobileActivity(): Promise<MobileActivity[]> {
    const data = await this.getLiveOperations();
    return data.mobileActivity;
  }

  /**
   * Clear cache to force refresh on next request
   */
  clearCache(): void {
    this.cache = null;
    this.lastFetch = 0;
  }

  /**
   * Set cache timeout
   */
  setCacheTimeout(timeoutMs: number): void {
    this.cacheTimeout = timeoutMs;
  }

  /**
   * Start polling for live updates
   */
  startLiveUpdates(
    onUpdate: (data: LiveOperationsData) => void,
    intervalMs: number = 10000
  ): () => void {

    const poll = async () => {
      try {
        const data = await this.getLiveOperations(true); // Force refresh
        onUpdate(data);
      } catch (error) {
        console.error('❌ Error during live operations polling:', error);
      }
    };

    // Initial poll
    poll();

    // Set up interval
    const intervalId = setInterval(poll, intervalMs);

    // Return cleanup function
    return () => {
      clearInterval(intervalId);
    };
  }

  /**
   * Get fallback data when API is unavailable
   */
  private getFallbackData(): LiveOperationsData {
    
    return {
      metrics: {
        activeRentals: 0,
        pendingHires: 0,
        activeDisputes: 0,
        totalRevenue24h: 0,
        averageTip: 0,
        fleetUtilization: 0,
        customerSatisfaction: 0,
        avgResponseTime: 0
      },
      locations: [],
      mobileActivity: [],
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Format metrics for display
   */
  formatMetricsForDisplay(metrics: LiveMetrics) {
    return [
      {
        title: 'Active Rentals',
        value: metrics.activeRentals,
        change: 12,
        changeType: 'increase' as const,
        subtitle: 'Currently rented',
        progress: Math.min((metrics.activeRentals / 50) * 100, 100)
      },
      {
        title: 'Pending Hires',
        value: metrics.pendingHires,
        change: 3,
        changeType: 'decrease' as const,
        subtitle: 'Awaiting verification',
        progress: Math.min((metrics.pendingHires / 25) * 100, 100)
      },
      {
        title: 'Disputes',
        value: metrics.activeDisputes,
        change: 0,
        changeType: 'decrease' as const,
        subtitle: 'Active cases',
        progress: Math.min((metrics.activeDisputes / 20) * 100, 100)
      },
      {
        title: 'Revenue (24h)',
        value: `₦${(metrics.totalRevenue24h / 1000).toFixed(1)}k`,
        change: 15,
        changeType: 'increase' as const,
        subtitle: 'Last 24 hours'
      },
      {
        title: 'Avg. Tip',
        value: `₦${metrics.averageTip}`,
        change: 8,
        changeType: 'increase' as const,
        subtitle: 'Per completed trip'
      },
      {
        title: 'Fleet Utilization',
        value: `${metrics.fleetUtilization}%`,
        change: 5,
        changeType: 'increase' as const,
        subtitle: 'Vehicles in use',
        progress: metrics.fleetUtilization
      },
      {
        title: 'Customer Satisfaction',
        value: metrics.customerSatisfaction.toFixed(1),
        change: 2,
        changeType: 'increase' as const,
        subtitle: 'Average rating',
        progress: (metrics.customerSatisfaction / 5) * 100
      },
      {
        title: 'Response Time',
        value: `${metrics.avgResponseTime}h`,
        change: 8,
        changeType: 'decrease' as const,
        subtitle: 'Avg. SLA response'
      }
    ];
  }
}

// Export singleton instance
export const liveOperationsService = new LiveOperationsService();
export default liveOperationsService;